import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, ChevronRight, ChevronDown } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useGuide, useGuideCategoriesBySlug, useGuideTerms } from '../hooks/useGuides';
import { GuideTermModal } from '../components/GuideTermModal';
import type { GuideTerm } from '../types';

export function GlossarDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('guides');
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTerm, setSelectedTerm] = useState<GuideTerm | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const { data: guide, isLoading: isLoadingGuide } = useGuide(slug || '');
  const { data: categories, isLoading: isLoadingCategories } = useGuideCategoriesBySlug(slug || '');
  const { data: terms, isLoading: isLoadingTerms } = useGuideTerms(guide?.id || '');

  // Handle direct link to term via URL param
  useEffect(() => {
    const termId = searchParams.get('term');
    if (termId && terms) {
      const term = terms.find((t) => t.id === termId);
      if (term) {
        setSelectedTerm(term);
        // Expand the category
        if (term.category_id) {
          setExpandedCategories((prev) => new Set([...prev, term.category_id!]));
        }
        // Clean up URL
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, terms, setSearchParams]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Group terms by category
  const termsByCategory = terms?.reduce((acc, term) => {
    const catId = term.category_id || 'uncategorized';
    if (!acc[catId]) acc[catId] = [];
    acc[catId].push(term);
    return acc;
  }, {} as Record<string, GuideTerm[]>) || {};

  const isLoading = isLoadingGuide || isLoadingCategories || isLoadingTerms;

  if (isLoading) {
    return (
      <>
        <h1 className="text-white text-xl font-semibold mb-6">{t('breadcrumbs.home')}</h1>
        <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
          <BookOpen className="w-4 h-4" />
          <span>{t('breadcrumbs.glossary')}</span>
          <ChevronRight className="w-4 h-4" />
          <div className="h-4 bg-white/20 rounded w-24 animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="glass-effect rounded-[15px] p-4 animate-pulse">
              <div className="h-6 bg-white/10 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      </>
    );
  }

  if (!guide) {
    return (
      <>
        <h1 className="text-white text-xl font-semibold mb-6">{t('breadcrumbs.home')}</h1>
        <div className="text-center py-12">
          <p className="text-white/70">{t('glossarDetail.notFound')}</p>
          <button
            onClick={() => navigate('/tools/guides/glossar')}
            className="mt-4 text-pp-orange hover:text-pp-orange/80"
          >
            {t('glossarDetail.backToOverview')}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-6">{t('breadcrumbs.home')}</h1>

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-white/80 text-sm mb-8">
        <BookOpen className="w-4 h-4" />
        <span>{t('breadcrumbs.glossary')}</span>
        <ChevronRight className="w-4 h-4" />
        <span>{guide.title}</span>
      </div>

      {/* Categories with Terms */}
      <div className="space-y-3">
        {categories?.map((category) => {
          const categoryTerms = termsByCategory[category.id] || [];
          const isExpanded = expandedCategories.has(category.id);

          if (categoryTerms.length === 0) return null;

          return (
            <div key={category.id} className="glass-effect rounded-[15px] overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-4 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-white">{category.name}</span>
                  <span className="text-xs text-pp-dark bg-white px-2 py-0.5 rounded-[2.5px]">
                    {categoryTerms.length}
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-white/60" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-pp-orange" />
                )}
              </button>

              {/* Terms List */}
              {isExpanded && (
                <div className="border-t border-white/10">
                  {categoryTerms.map((term, idx) => (
                    <button
                      key={term.id}
                      onClick={() => setSelectedTerm(term)}
                      className={`w-full px-6 py-3 text-left hover:bg-white/5 transition-colors flex items-center justify-between ${
                        idx !== categoryTerms.length - 1 ? 'border-b border-white/10' : ''
                      }`}
                    >
                      <span className="text-white/90">{term.term}</span>
                      <ChevronRight className="w-4 h-4 text-pp-orange" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Uncategorized Terms */}
        {termsByCategory['uncategorized']?.length > 0 && (
          <div className="glass-effect rounded-[15px] overflow-hidden">
            <button
              onClick={() => toggleCategory('uncategorized')}
              className="w-full px-4 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="font-medium text-white">{t('glossarDetail.uncategorized')}</span>
                <span className="text-xs text-pp-dark bg-white px-2 py-0.5 rounded-[2.5px]">
                  {termsByCategory['uncategorized'].length}
                </span>
              </div>
              {expandedCategories.has('uncategorized') ? (
                <ChevronDown className="w-5 h-5 text-white/60" />
              ) : (
                <ChevronRight className="w-5 h-5 text-pp-orange" />
              )}
            </button>

            {expandedCategories.has('uncategorized') && (
              <div className="border-t border-white/10">
                {termsByCategory['uncategorized'].map((term, idx) => (
                  <button
                    key={term.id}
                    onClick={() => setSelectedTerm(term)}
                    className={`w-full px-6 py-3 text-left hover:bg-white/5 transition-colors flex items-center justify-between ${
                      idx !== termsByCategory['uncategorized'].length - 1 ? 'border-b border-white/10' : ''
                    }`}
                  >
                    <span className="text-white/90">{term.term}</span>
                    <ChevronRight className="w-4 h-4 text-pp-orange" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Term Detail Modal */}
      {selectedTerm && (
        <GuideTermModal
          term={selectedTerm}
          onClose={() => setSelectedTerm(null)}
        />
      )}
    </>
  );
}
